import { QueryKey } from "@/api";
import { Api } from "@/api/Api";
import { fetchSlimProfiles } from "@/api/hooks/profile";
import { fetchTab } from "@/api/hooks/tab";
import { useProfileAndTabParams } from "@/hooks";
import { useTimeService } from "@/models/time-control/timeStateHooks";
import { type RootState, useAppDispatch } from "@/reducer";
import reduxStore from "@/reducer";
import { setAiChatProcessing, setInteractionLock } from "@/reducer/UIState";
import type { AiChatMessage, AiChatRequest } from "@/types/ai-chat";
import { buildTabSnapshot } from "@/utils/tabSnapshot";
import { t } from "@lingui/core/macro";
import type { TabSnapshot } from "@mm/metx-workbench.meteomatics.com";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { type AiChatActionContext, createAiChatActionExecutor } from "./aiChatActionHandlers";
import { useAiChatSession } from "./useAiChatSession";
import { type AiChatRefreshPayload, useAiChatStream } from "./useAiChatStream";
import { VOICE_FILE_NAME } from "./useVoiceRecorder";

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createUserMessage = (content: string): AiChatMessage => ({
  id: `user-${uid()}`,
  role: "user",
  content,
});

const createAssistantMessage = (): AiChatMessage => ({
  id: `asst-${uid()}`,
  role: "assistant",
  content: "",
});

interface UseAiChatProps {
  conversationId: string | null;
  contextMessage?: string | null;
  metadata?: Record<string, unknown> | null;
  tabSnapshot?: TabSnapshot | null;
}

type StreamCall = (payload: AiChatRequest, signal: AbortSignal) => Promise<Response>;

export function useAiChat({ conversationId, contextMessage, metadata, tabSnapshot }: UseAiChatProps) {
  const dispatch = useAppDispatch();
  const isUiLocked = useSelector((state: RootState) => state.ui.isInteractionLocked);
  const queryClient = useQueryClient();
  const { profileId: activeProfileId } = useProfileAndTabParams();
  const { timeService } = useTimeService();

  const {
    messages,
    setMessages,
    conversationIdState,
    setConversationIdState,
    processedActionIdsRef,
    applyMessageMeta,
    markActionsProcessed,
    resetSession,
  } = useAiChatSession();

  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abortCtrl, setAbortCtrl] = useState<AbortController | null>(null);

  const messagesRef = useRef<AiChatMessage[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const { streamAiChatResponse } = useAiChatStream({ setMessages, applyMessageMeta });

  const getCurrentTabSnapshot = useCallback(() => buildTabSnapshot(reduxStore.getState().tabGroup.present), []);

  const actionContext = useMemo<AiChatActionContext>(
    () => ({
      dispatch,
      queryClient,
      timeService,
      activeProfileId,
      getCurrentTabSnapshot,
      markMessageMeta: applyMessageMeta,
    }),
    [dispatch, queryClient, timeService, activeProfileId, getCurrentTabSnapshot, applyMessageMeta],
  );

  const executeActions = useMemo(() => createAiChatActionExecutor(actionContext), [actionContext]);

  const canSend = useMemo(() => input.trim().length > 0 && !isStreaming, [input, isStreaming]);

  const reportError = useCallback((message: string) => {
    setError(message);
  }, []);

  const buildRequestPayload = useCallback(
    (userMsg: AiChatMessage): AiChatRequest => {
      if (!conversationIdState && conversationId) {
        setConversationIdState(conversationId);
      }

      const payloadMessages: AiChatMessage[] = [...messagesRef.current];
      if (contextMessage) {
        payloadMessages.push({ id: `ctx-${uid()}`, role: "system", content: contextMessage });
      }
      payloadMessages.push(userMsg);

      const payload: AiChatRequest = {
        conversationId: conversationIdState ?? conversationId ?? null,
        messages: payloadMessages,
      };

      if (metadata && Object.keys(metadata).length > 0) {
        payload.metadata = { ...metadata };
      }

      return payload;
    },
    [conversationId, conversationIdState, contextMessage, metadata, setConversationIdState],
  );

  const applyRefreshPayload = useCallback(
    (refreshPayload: AiChatRefreshPayload | null) => {
      if (!refreshPayload) {
        return;
      }

      if (refreshPayload.profiles) {
        dispatch(fetchSlimProfiles());
      }

      const timeStateProfileId = refreshPayload.time_state?.profile_id;
      if (timeStateProfileId) {
        queryClient.invalidateQueries({ queryKey: [QueryKey.TIME_STATE, timeStateProfileId] }).catch(() => {});
      }

      const tabIds = Array.isArray(refreshPayload.tabs) ? refreshPayload.tabs : [];
      const fallbackTabId =
        tabSnapshot?.tab_meta?.id ??
        (metadata?.activeTabId as number | undefined) ??
        (metadata?.active_tab_id as number | undefined) ??
        (metadata?.tabId as number | undefined) ??
        (metadata?.tab_id as number | undefined);

      const uniqueTabIds = new Set<number>(tabIds);
      if (uniqueTabIds.size === 0 && fallbackTabId) {
        uniqueTabIds.add(fallbackTabId);
      }

      for (const tabId of uniqueTabIds) {
        dispatch(fetchTab({ tabId }));
      }
    },
    [dispatch, metadata, queryClient, tabSnapshot],
  );

  const runStream = useCallback(
    async (userMsg: AiChatMessage, assistantMsg: AiChatMessage, streamCall: StreamCall) => {
      const controller = new AbortController();
      setAbortCtrl(controller);
      setIsStreaming(true);
      dispatch(setAiChatProcessing(true));

      let lockedByRequest = false;
      try {
        const payload = buildRequestPayload(userMsg);
        const rawResponse = await streamCall(payload, controller.signal);

        const intentHeader = rawResponse.headers?.get("X-AI-Intent");
        if (intentHeader === "metx_interaction" && !lockedByRequest && !isUiLocked) {
          dispatch(setInteractionLock(true));
          lockedByRequest = true;
        }

        const { refreshPayload, messageMeta } = await streamAiChatResponse(rawResponse, assistantMsg.id, userMsg.id);

        const actions = Array.isArray(messageMeta?.actions) ? messageMeta.actions : [];
        if (actions.length > 0 && !processedActionIdsRef.current.has(assistantMsg.id)) {
          processedActionIdsRef.current.add(assistantMsg.id);
          const undoIndexBefore = reduxStore.getState().tabGroup.past.length;

          await executeActions(actions, assistantMsg.id);
          markActionsProcessed(assistantMsg.id);
          applyMessageMeta(assistantMsg.id, { revertUndoIndex: undoIndexBefore });
        }

        applyRefreshPayload(refreshPayload);
      } catch (err: unknown) {
        if ((err as any)?.name === "AbortError") {
          return;
        }

        const message = err instanceof Error ? err.message : String(err);
        setError(message || "Streaming failed");
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.id === assistantMsg.id) {
            next[next.length - 1] = {
              ...last,
              content: last.content.trim() === "" ? "(Failed to load response.)" : last.content,
            };
          }
          return next;
        });
      } finally {
        if (lockedByRequest) {
          dispatch(setInteractionLock(false));
        }
        setIsStreaming(false);
        setAbortCtrl(null);
        dispatch(setAiChatProcessing(false));
      }
    },
    [
      applyMessageMeta,
      applyRefreshPayload,
      buildRequestPayload,
      dispatch,
      executeActions,
      isUiLocked,
      markActionsProcessed,
      processedActionIdsRef,
      streamAiChatResponse,
    ],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) {
        return;
      }

      setError(null);

      const userMsg = createUserMessage(trimmed);
      const assistantMsg = createAssistantMessage();
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      await runStream(userMsg, assistantMsg, async (payload, signal) => {
        const response = await Api.aiChat.streamAiChatApiV2AiChatStreamPostRaw({ aiChatRequest: payload }, { signal });
        return response.raw;
      });
    },
    [isStreaming, runStream, setMessages],
  );

  const sendAudio = useCallback(
    async (audio: Blob) => {
      if (!audio || isStreaming) {
        return;
      }

      setError(null);

      const userMsg = createUserMessage(t`(Transcribing…)`);
      const assistantMsg = createAssistantMessage();
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      await runStream(userMsg, assistantMsg, async (payload, signal) => {
        const file = new File([audio], VOICE_FILE_NAME, { type: audio.type || "audio/webm" });
        const response = await Api.aiChat.streamAiChatAudioApiV2AiChatStreamAudioPostRaw(
          { payload: JSON.stringify(payload), file },
          { signal },
        );
        return response.raw;
      });
    },
    [isStreaming, runStream, setMessages],
  );

  const handleResetConversation = useCallback(() => {
    resetSession();
    setInput("");
    setError(null);
    dispatch(setInteractionLock(false));
  }, [dispatch, resetSession]);

  const handleCancel = useCallback(() => {
    if (!abortCtrl) {
      return;
    }
    abortCtrl.abort();
    setAbortCtrl(null);
    setIsStreaming(false);
    setError("Request canceled by user.");
  }, [abortCtrl]);

  useEffect(() => {
    return () => {
      abortCtrl?.abort();
    };
  }, [abortCtrl]);

  return {
    messages,
    input,
    setInput,
    setMessages,
    isStreaming,
    error,
    canSend,
    send,
    sendAudio,
    reportError,
    handleCancel,
    resetConversation: handleResetConversation,
  };
}
